import { useState } from 'react';

import type { LookalikeSchema, LookalikeWeightUpdate } from '@repo/contracts';
import {
  LOOKALIKE_WEIGHT_FIELDS,
  type LookalikeWeightField,
} from '@repo/contracts/lookalike-schema';

import { THIN_SCROLLBAR } from './lookalike-scrollbar';

const FIELD_LABELS: Record<LookalikeWeightField, string> = {
  brandLoyalty: 'Brand loyalty',
  companySizes: 'Company size',
  goals: 'Goals',
  jobTitles: 'Job title',
  locations: 'Location',
  painPoints: 'Pain points',
  techSavviness: 'Tech savviness',
};

type LocalWeights = Record<string, number>;

function keyOf(field: LookalikeWeightField, value: string): string {
  return `${field}:${value}`;
}

function initialWeights(schema: LookalikeSchema): LocalWeights {
  const weights: LocalWeights = {};
  for (const field of LOOKALIKE_WEIGHT_FIELDS) {
    for (const entry of schema[field])
      weights[keyOf(field, entry.value)] = entry.weight;
  }
  return weights;
}

/**
 * One slider per pool value, across every adjustable field — see
 * `LookalikeSchema`'s own doc: a weight is a sampling probability, not a
 * percentage that has to sum to 100, so raising one slider doesn't need to
 * shrink its siblings for the population to visibly shift toward it.
 *
 * Deliberately two-phase: dragging a slider only ever changes what's on
 * screen in this panel — no re-sample, no request, nothing else moves. The
 * population only changes when "Apply changes" is pressed, in one batched
 * PATCH covering every slider moved since the last apply. `key`d by the
 * parent on `spec.updatedAt`, so a successful apply (or a fresh `generate()`,
 * which also bumps it) remounts this with the *new* schema as the fresh
 * baseline — that's what clears the pending count back to zero.
 */
export function LookalikesWeights({
  applying,
  onApply,
  schema,
}: {
  /** True while the batched PATCH from a previous "Apply changes" is still
   *  in flight — sliders lock so a second batch can't race the first. */
  applying: boolean;
  onApply: (updates: LookalikeWeightUpdate[]) => void;
  schema: LookalikeSchema;
}) {
  const [weights, setWeights] = useState<LocalWeights>(() =>
    initialWeights(schema),
  );
  const [pending, setPending] = useState<Map<string, LookalikeWeightUpdate>>(
    () => new Map(),
  );

  const handleSlide = (
    field: LookalikeWeightField,
    value: string,
    weight: number,
  ) => {
    const key = keyOf(field, value);
    setWeights((current) => ({ ...current, [key]: weight }));
    setPending((current) => {
      const next = new Map(current);
      next.set(key, { field, value, weight });
      return next;
    });
  };

  const pendingCount = pending.size;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className={`min-h-0 flex-1 overflow-y-auto pr-1 ${THIN_SCROLLBAR}`}>
        <div className="flex flex-col gap-5">
          {LOOKALIKE_WEIGHT_FIELDS.map((field) => (
            <div key={field}>
              <p className="text-small-label text-text-muted uppercase">
                {FIELD_LABELS[field]}
              </p>
              <div className="mt-2 flex flex-col gap-2.5">
                {schema[field].map((entry) => {
                  const key = keyOf(field, entry.value);
                  const weight = weights[key] ?? entry.weight;
                  const dirty = pending.has(key);
                  return (
                    <div key={entry.value}>
                      <div className="flex items-baseline justify-between gap-2">
                        <span
                          className="min-w-0 truncate text-caption text-text-secondary"
                          title={entry.value}
                        >
                          {entry.value}
                        </span>
                        <span
                          className={`shrink-0 font-mono text-tag tabular-nums ${dirty ? 'font-semibold text-brand' : 'text-text-muted'}`}
                        >
                          {Math.round(weight)}
                        </span>
                      </div>
                      <input
                        aria-label={`${FIELD_LABELS[field]}: ${entry.value}`}
                        className="mt-1 h-1.5 w-full cursor-pointer accent-brand disabled:cursor-default disabled:opacity-50"
                        disabled={applying}
                        max={100}
                        min={0}
                        onChange={(event) => {
                          handleSlide(
                            field,
                            entry.value,
                            Number(event.target.value),
                          );
                        }}
                        type="range"
                        value={weight}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="shrink-0 border-t border-hairline pt-3">
        <button
          className="flex h-9 w-full cursor-pointer items-center justify-center gap-1.5 rounded-button bg-brand text-ui font-semibold text-white transition hover:bg-brand-alt active:scale-[0.98] disabled:cursor-default disabled:bg-hairline-alt disabled:text-text-muted disabled:active:scale-100"
          disabled={pendingCount === 0 || applying}
          onClick={() => {
            onApply([...pending.values()]);
          }}
          type="button"
        >
          {applying
            ? 'Applying…'
            : pendingCount === 0
              ? 'No changes'
              : `Apply ${pendingCount} change${pendingCount === 1 ? '' : 's'}`}
        </button>
      </div>
    </div>
  );
}
